<template>
  <!-- 表单视图：新建/查看/编辑系统消息 -->
  <system-messages
    v-if="showForm"
    :msg-id="currentId"
    :view-mode="viewMode"
    @back="handleBackFromForm"
  />

  <!-- 列表视图 -->
  <div v-else class="page-container">
    <!-- 顶部操作栏 -->
    <div class="top-bar">
      <div class="title-area">
        <h2>{{ $t('home.systemMessageConfigList') }}</h2>
        <p>{{ $t('home.systemMessageDesc') }}</p>
      </div>
      <el-button class="add-btn" type="primary" @click="handleCreate">
        + {{ $t('home.createSystemMessage') }}
      </el-button>
    </div>
    <!-- 搜索条件区域 -->
    <el-card class="search-card" shadow="never">
      <el-form
        :model="searchForm" class="search-form" label-position="top"
        size="small"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <!-- 创建时间（对应 createStartTime / createEndTime） -->
            <el-form-item :label="$t('home.creationTime')">
              <el-date-picker
                v-model="timeValue"
                :clearable="false"
                :default-time="['00:00:00', '23:59:59']"
                :end-placeholder="$t('home.endTime')"
                :start-placeholder="$t('home.startTime')"
                range-separator="~"
                style="width: 100%"
                type="datetimerange"
                value-format="yyyy-MM-dd HH:mm:ss"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item :label="$t('home.messageType')">
              <el-select
                v-model="searchForm.messageType"
                :placeholder="$t('home.all')"
                clearable
                style="width: 100%"
              >
                <el-option :label="$t('home.urgentAnnouncement')" :value="15" />
                <el-option :label="$t('home.operationNotice')" :value="14" />
                <el-option :label="$t('home.systemUpdate')" :value="1" />
                <el-option :label="$t('home.normalNotice')" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item :label="$t('home.title')">
              <el-input
                v-model="searchForm.title"
                :maxlength="200"
                :placeholder="$t('home.inputTitleKeyword')"
                clearable
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item :label="$t('home.status')">
              <el-select
                v-model="searchForm.status"
                :placeholder="$t('home.all')"
                clearable
                style="width: 100%"
              >
                <el-option :label="$t('home.pendingPublish')" :value="1" />
                <el-option :label="$t('home.enabled')" :value="2" />
                <el-option :label="$t('home.disabled')" :value="3" />
                <el-option :label="$t('home.draft')" :value="4" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="6">
            <el-form-item :label="$t('home.system')">
              <el-select
                v-model="searchForm.systemType"
                :placeholder="$t('home.all')"
                clearable
                style="width: 100%"
                @change="handleSystemTypeChange"
              >
                <el-option :label="$t('home.allSystems')" :value="0" />
                <el-option :label="$t('home.expressSystem')" :value="1" />
                <el-option :label="$t('home.providerSystem')" :value="2" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item :label="$t('home.receiveArea')">
              <el-select
                v-model="searchForm.regionId"
                :placeholder="$t('home.selectArea')"
                clearable
                style="width: 100%"
              >
                <el-option :label="$t('home.all')" :value="-1" />
                <el-option
                  v-for="item in regionList"
                  :key="item.id"
                  :label="item.zhName + '（' + item.puName + '）'"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item :label="$t('home.receiveState')">
              <el-select
                v-model="searchForm.receiveStateList"
                :placeholder="$t('home.selectState')"
                clearable
                collapse-tags
                filterable
                multiple
                style="width: 100%"
              >
                <el-option :label="$t('home.all')" value="-1" />
                <el-option
                  v-for="(item, index) in stateData"
                  :key="index"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item :label="$t('home.receiveSite')">
              <el-select
                v-model="searchForm.receiveSiteList"
                :placeholder="$t('home.selectSite')"
                clearable
                collapse-tags
                filterable
                multiple
                style="width: 100%"
              >
                <el-option :label="$t('home.all')" :value="-1" />
                <el-option
                  v-for="item in siteList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item :label="$t('home.receiveUser')">
              <el-select
                v-model="searchForm.receiveUserList"
                :disabled="!searchForm.systemType"
                :placeholder="$t('home.selectUserAccount')"
                :remote-method="remoteSearchUser"
                clearable
                collapse-tags
                filterable
                multiple
                remote
                reserve-keyword
                style="width: 100%"
                @change="(val) => handleSelectChange('userList', val)"
                @clear="handleClearUser"
              >
                <el-option
                  v-for="item in userOptions"
                  :key="item.id"
                  :label="item.username"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item :label="$t('home.creator')">
              <el-select
                v-model="searchForm.createUser"
                :placeholder="$t('home.pleaseChoose')"
                :remote-method="getUserManageOption"
                clearable
                filterable
                remote
                reserve-keyword
                style="width: 100%"
                @visible-change="handleUserManageOption"
              >
                <el-option
                  v-for="item in createrList"
                  :key="item.id"
                  :label="item.realName"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <!-- 按钮组，布局到最右侧 -->
          <el-col :span="12" style="margin-top: 27px">
            <el-button size="small" @click="handleReset">{{ $t('home.reset') }}</el-button>
            <el-button size="small" type="primary" @click="enterSearch">{{ $t('home.search') }}
            </el-button>
          </el-col>
        </el-row>
      </el-form>
    </el-card>
    <!-- 配置列表区域 -->
    <div class="list-container">
      <div class="list-header">
        <span>{{ $t('home.totalConfigs') }} {{ queryForm.total }} {{ $t('home.configsUnit') }}</span>
      </div>
      <el-table :data="tableData" border style="width: 100%">
        <el-table-column :label="$t('home.creationTime')" prop="createDate" width="180" />
        <!-- 公告类型列：添加 el-tag 不同主题色 -->
        <el-table-column :label="$t('home.messageType')" prop="typeName" width="100">
          <template #default="{ row }">
            <div :class="['custom-type-tag', getTypeTagClass(row.type)]">
              {{ row.typeName }}
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('home.title')" min-width="200">
          <template #default="{ row }">
            <el-tooltip effect="dark" placement="top">
              <!-- 悬浮显示的内容：两行 -->
              <div slot="content">
                <div style="margin-bottom: 4px; font-weight: bold">
                  {{ $t('home.chinese') }}：{{ row.zhTitle }}
                </div>
                <div>{{ $t('home.portuguese') }}：{{ row.title }}</div>
              </div>
              <!-- 表格中显示的内容 -->
              <div class="title-cell">
                <div class="title-zh">{{ row.zhTitle }}</div>
                <div class="title-pt">{{ row.title }}</div>
              </div>
            </el-tooltip>
          </template>
        </el-table-column>
        <!-- 状态列：改为带背景色和字色的状态标签 -->
        <el-table-column :label="$t('home.status')" width="100">
          <template #default="{ row }">
            <span :class="['status-badge', `status-${row.status}`]">
              <i v-if="row.status === 2" class="el-icon-circle-check status-icon" />
              <i v-else-if="row.status === 1" class="el-icon-time status-icon" />
              <i v-else-if="row.status === 4" class="el-icon-document status-icon" />
              <i v-else-if="row.status === 3" class="el-icon-circle-close status-icon" />
              {{ row.statusName }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('home.system')"
          prop="systemTypeName"
          show-overflow-tooltip
          width="150"
        />
        <el-table-column :label="$t('home.displayPosition')" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="display-pos">{{ row.displayLocatNameList.join(',') }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('home.userEnd')" align="center" width="80">
          <template #default="{ row }">
            {{ row.userTypeName || '--' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('home.userRange')" align="center" width="100">
          <template #default="{ row }">
            {{ row.receptTypeName || '--' }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('home.receiveAreaCount')"
          align="center"
          show-overflow-tooltip
          width="100"
        >
          <template #default="{ row }">
            {{ row.receiveAreaName || '--' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('home.receiveStateCount')" align="center" width="80">
          <template #default="{ row }">
            {{ row.receiveStateCount || '--' }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('home.receiveSiteCount')"
          align="center"
          show-overflow-tooltip
          width="100"
        >
          <template #default="{ row }">
            {{ row.receiveSiteCount || '--' }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('home.receiveUserCount')"
          align="center"
          show-overflow-tooltip
          width="100"
        >
          <template #default="{ row }">
            {{ row.receiveUserCount || '--' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('home.publishTime')" align="center" width="180">
          <template #default="{ row }">
            {{ row.effectiveTime || '--' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('home.expirationTime')" align="center" width="180">
          <template #default="{ row }">
            {{ row.failureTime || '--' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('home.creator')" align="center" width="100">
          <template #default="{ row }">
            {{ row.createUserName || '--' }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('home.operation')" align="center" fixed="right"
          width="180"
        >
          <template #default="{ row }">
            <el-button size="small" type="text" @click="handleView(row)">
              {{ $t('home.view') }}
            </el-button>
            <el-button
              v-if="row.status === 1 || row.status === 4"
              size="small"
              type="text"
              @click="handleEdit(row)"
            >
              {{ $t('home.edit') }}
            </el-button>
            <el-button
              v-if="row.status === 1 || row.status === 2"
              size="small"
              style="color: #f56c6c"
              type="text"
              @click="disableData(row)"
            >
              {{ $t('home.disable') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页区域 -->
      <div class="pagination-footer">
        <el-pagination
          :current-page="queryForm.pageNumber"
          :page-size="queryForm.pageSize"
          :page-sizes="[10, 50, 100, 500, 1000]"
          :total="queryForm.total"
          background
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { selectProviderMessage, updateNoticeStatus } from '@/api/newExpressAPI';
import dayjs from 'dayjs';
import { getLgAreaListData, getUserList, regionQueryList } from '@/api/orderApi';
import { getAllsite } from '@/api/logisticsOrders';
import SystemMessages from '../systemMessages/index.vue';
import { query10User } from '@/api/lanshou';

export default {
  name: 'NotificationList',
  components: {
    SystemMessages
  },
  data() {
    return {
      // 是否展示新建表单视图
      showForm: false,
      // 当前操作的记录 id（查看/编辑时使用，新建为空）
      currentId: null,
      // 视图模式：create 新建 / view 查看 / edit 编辑
      viewMode: 'create',
      // 搜索表单数据（字段名对齐接口 params）
      searchForm: {
        title: '',
        status: null,
        systemType: null,
        regionId: null,
        receiveStateList: [],
        receiveSiteList: [],
        receiveUserList: [],
        createUser: null,
        messageType: null
      },
      timeValue: [],
      // 表格数据
      tableData: [],
      queryForm: {
        pageSize: 10,
        pageNumber: 1,
        total: 0
      },
      regionList: [],
      stateData: [],
      siteList: [],
      userOptions: [],
      createrList: []
    };
  },
  mounted() {
    this.getTimer();
    this.getSearch();
    this.getState();
    this.getRegion();
    this.getAllSite();
    this.getUserManageOption();
  },
  methods: {
    // 公告类型对应的 Tag 类型
    getTypeTagClass(type) {
      switch (type) {
        case 15: // 紧急公告
          return 'tag-danger-deep';
        case 14: // 运营通知
          return 'tag-warning-deep';
        case 1: // 系统更新
          return 'tag-success-deep';
        case 2: // 普通通知
          return 'tag-normal-deep';
        default:
          return 'tag-normal-deep';
      }
    },
    // 获取所有网点
    getAllSite() {
      getAllsite().then((res) => {
        if (res.code === 200) {
          console.log('获取所有网点', res.data);
          this.siteList = res.data;
        }
      });
    },
    getState() {
      const params = {
        level: 0,
        parentId: ''
      };
      getLgAreaListData(params, false).then((res) => {
        if (res.code === 200) {
          this.stateData = res.data.map((item) => {
            return {
              label: item.nameEn
            };
          });
        }
      });
    },
    async getRegion() {
      const res = await regionQueryList(3);
      if (res.code === 200) {
        this.regionList = res.data;
      }
    },
    // 新建系统消息，切换到表单视图
    handleCreate() {
      this.currentId = null;
      this.viewMode = 'create';
      this.showForm = true;
    },
    // 查看系统消息详情
    handleView(row) {
      this.currentId = row.id;
      this.viewMode = 'view';
      this.showForm = true;
    },
    // 编辑系统消息
    handleEdit(row) {
      this.currentId = row.id;
      this.viewMode = 'edit';
      this.showForm = true;
    },
    // 从表单视图返回列表，并刷新数据
    handleBackFromForm() {
      this.showForm = false;
      this.currentId = null;
      this.viewMode = 'create';
      this.getSearch();
    },
    handleReset() {
      this.searchForm = {
        title: '',
        status: null,
        systemType: null,
        regionId: null,
        receiveStateList: [],
        receiveSiteList: [],
        receiveUserList: [],
        createUser: null,
        messageType: null
      };
      this.getTimer();
      this.enterSearch();
    },
    enterSearch() {
      this.queryForm.pageNumber = 1;
      this.getSearch();
    },
    async getSearch() {
      // 组装请求参数，字段对齐接口 params
      const [createStartTime, createEndTime] = this.timeValue || [];
      const data = {
        params: {
          title: this.searchForm.title || null,
          status: this.searchForm.status ?? null,
          systemType: this.searchForm.systemType ?? null,
          regionId: this.searchForm.regionId || null,
          receiveStateList: this.searchForm.receiveStateList || [],
          receiveSiteList: this.searchForm.receiveSiteList || [],
          receiveUserList: this.searchForm.receiveUserList || [],
          createUser: this.searchForm.createUser || null,
          messageType: this.searchForm.messageType || null,
          createStartTime: createStartTime || null,
          createEndTime: createEndTime || null
        },
        pageSize: this.queryForm.pageSize,
        pageNumber: this.queryForm.pageNumber
      };
      console.log('执行搜索，参数:', data);
      const result = await selectProviderMessage(data);
      if (result.code === 200) {
        this.tableData = result.data.records;
        this.queryForm.total = result.data.total;
      }
      console.log(result, 'resultresult');
    },
    // 获取入库人选项
    getUserManageOption(val = '') {
      query10User(val).then((res) => {
        if (res.code === 200) {
          this.createrList = res.data;
        }
      });
    },
    handleUserManageOption(val) {
      if (val) {
        this.getUserManageOption();
      }
    },
    async getUserList(fuzzyFind = '') {
      try {
        const data = {
          providerList: [],
          siteList: [],
          systemType: this.searchForm.systemType || 1,
          fuzzyFind: fuzzyFind
        };
        const res = await getUserList(data);
        if (res.code === 200) {
          const list = res.data.pmUsers || res.data.users || [];
          this.userOptions = list;
        }
      } finally {
        this.userLoading = false;
      }
    },
    handleSystemTypeChange(val) {
      this.searchForm.receiveUserList = [];
      if (val) {
        this.getUserList();
      }
    },
    // 远程搜索用户
    remoteSearchUser(query) {
      if (query !== '') {
        this.getUserList(query);
      } else {
        this.userOptions = [];
      }
    },
    // 点击清空按钮时，重新加载完整用户列表，方便重新选择
    handleClearUser() {
      this.getUserList();
    },
    // 处理「全部」+ 数量上限：选中全部时锁定；否则最多选 max 个
    handleSelectChange(field, val) {
      console.log(val, 'handleSelectChangehandleSelectChange');
      const list = val || [];
      const hasAll = list.includes(-1);
      // 1. 「全部」锁定：只要包含「全部」就只保留「全部」
      if (hasAll) {
        if (list.length > 1) {
          this.searchForm[field] = [-1];
        }
        return;
      }
      if (list.length === 0) {
        this.getUserList();
      }
    },
    // 点击分页
    handleSizeChange(val) {
      this.queryForm.pageSize = val;
      this.queryForm.pageNumber = 1;
      this.getSearch();
    },
    handleCurrentChange(val) {
      this.queryForm.pageNumber = val;
      this.getSearch();
    },
    // 停用 / 启用
    disableData(row) {
      // 可停用状态：1（待发布）、2（启用中）
      // 可启用状态：3（已停用）
      const isEnable = row.status === 3; // 状态3 → 启用操作
      const isDisable = row.status === 1 || row.status === 2; // 状态1/2 → 停用操作
      if (!isEnable && !isDisable) {
        this.$message.warning(this.$t('home.currentStatusNotSupport'));
        return;
      }
      const typeText = isEnable ? this.$t('home.confirmEnable') : this.$t('home.confirmDisable');
      this.$confirm(typeText, this.$t('home.tips'), {
        confirmButtonText: this.$t('home.confirm'),
        cancelButtonText: this.$t('home.cancel'),
        type: 'warning'
      })
        .then(() => {
          const data = {
            id: row.id,
            status: isEnable ? 2 : 3 // 启用设为2，停用设为3
          };
          updateNoticeStatus(data).then((res) => {
            if (res.code === 200) {
              this.$message.success(this.$t('home.operationSuccess'));
              this.getSearch();
            }
          });
        })
        .catch(() => {});
    },
    // 获取最近一星期时间
    getTimer() {
      const day = new Date();
      const brazilTime = day.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' });
      const today = dayjs(brazilTime).format('YYYY-MM-DD');
      this.timeValue = [today + ' 00:00:00', today + ' 23:59:59'];
    }
  }
};
</script>

<style scoped>
/* 整体容器 */
.page-container {
  background-color: #f6f8f9;
  min-height: 100vh;
  box-sizing: border-box;
}

/* 顶部标题栏 */
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.title-area h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1d2129;
}

.title-area p {
  margin: 6px 0 0;
  color: #86909c;
  font-size: 14px;
}

.add-btn {
  border-radius: 4px;
  padding: 8px 16px;
}

/* 搜索卡片 */
.search-card {
  margin-bottom: 10px;
  border-radius: 6px;
}

.search-form .el-form-item {
  margin-bottom: 12px;
}

/* 列表区域 */
.list-container {
  background: #fff;
  padding: 10px;
  border-radius: 6px;
}

/* 列表头部信息 */
.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  color: #4e5969;
  font-size: 14px;
}

.export-btn {
  background: #f2f3f5;
  border: none;
  color: #4e5969;
}

::v-deep .el-table__row {
  height: 60px;
}

/* 公告类型 Tag */
.type-tag {
  border-radius: 12px;
  padding: 0 12px;
  font-weight: normal;
}

/* 标题文字（中/葡） */
.title-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  line-height: normal;
}

.title-zh {
  font-size: 14px;
  font-weight: bold;
  color: #1d2129;
}

.title-pt {
  font-size: 12px;
  color: #86909c;
}

/* 状态 + 小圆点 */
.status-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.dot-published {
  background-color: #00b42a;
}

.dot-pending {
  background-color: #ff7d00;
}

.dot-draft {
  background-color: #86909c;
}

.dot-disabled {
  background-color: #f53f3f;
}

/* 展示位置 */
.display-pos {
  font-size: 13px;
  color: #4e5969;
}

/* 分页底部 */
.pagination-footer {
  display: flex;
  justify-content: right;
  align-items: center;
  margin-top: 10px;
}

/* 公告类型 Tag 样式 */
.type-tag {
  border-radius: 4px;
  border: none;
  font-weight: 500;
}

/* 状态标签通用样式 */
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-icon {
  margin-right: 4px;
}

/* 状态对应颜色：发布中（2）- 绿色系 */
.status-2 {
  background-color: #e8ffea;
  color: #00b42a;
}

/* 状态对应颜色：待发布（1）- 橙色系 */
.status-1 {
  background-color: #fff3e8;
  color: #ff7d00;
}

/* 状态对应颜色：草稿（4）- 灰色系 */
.status-4 {
  background-color: #f2f3f5;
  color: #86909c;
}

/* 状态对应颜色：已停用（3）- 红色系 */
.status-3 {
  background-color: #ffece8;
  color: #f53f3f;
}

/* 自定义公告类型标签 */
.custom-type-tag {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
}

/* 以下四个颜色类为指定效果 */
.tag-danger-deep {
  color: #ee3535;
  background: #ffe8e8;
}

.tag-warning-deep {
  color: #ff8a00;
  background: #fff3e0;
}

.tag-success-deep {
  color: #18824f;
  background: #c6e9d0;
}

.tag-normal-deep {
  color: #2b6fff;
  background: #e6efff;
}
</style>
